import { PlaycastMessage, PlaycastMessageStateReplication, PlaycastMessageTarget, generators } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { channel } from 'diagnostics_channel';
import * as Sentry from '@sentry/react';
import { getElectronStores } from '../../stores/electronStores';
import { getSharedStores } from '../../stores/shared';
import { addReplicationStateChangeCallback, getNotifyMessages } from '@playcastdotio/StateStore';

const PORT = 27123;
const HEARTBEAT_INTERVAL = 20000; // 20 seconds between pings (match server config)
const RECONNECT_TIMEOUT = 3000; // 3 seconds before reconnecting

let wsConnection: WebSocket | null = null;
let isConnected = false;
let messageQueue: PlaycastMessage<PlaycastMessageTarget>[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;
let shouldAutoReconnect = true;
let reconnectAttempts = 0;
const maxReconnectAttempts = 100;
const baseReconnectDelay = 1000; // 1 second base delay

// ============================================================================
// NODE-SERVICE CONNECTION CREDENTIAL
// ============================================================================
//
// node-service's WebSocket is a privileged local control surface and now
// requires a shared secret on every connection (see
// apps/node-service/src/websocketAuthToken.ts). node-service mints the token at
// start and publishes it to a DACL-restricted file under
// C:\ProgramData\Playcast\node-service\ws-auth-token.
//
// This renderer cannot read that file itself: the host window runs with
// `contextIsolation` at its Electron default (true), so the page has no Node
// APIs no matter what `nodeIntegration` says, and it is served from a remote
// origin (constants.DASHBOARD_BASE_URL). The token therefore has to arrive
// through host-electron's preload bridge, which is the only channel this
// context has. A browser WebSocket also cannot set request headers, so the
// credential goes on the URL as `?token=` — the query form node-service
// accepts precisely for this peer.
//
// NOTE: the preload field consumed below (`window.playcast.nodeServiceAuthToken`
// / `getNodeServiceAuthToken()`) does not exist yet — adding it is a small
// change to apps/host/host-electron/src/app/api/preload.ts plus a main-process
// read of the token file, and host-electron is outside this change's file set.
// Until it lands node-service will refuse this connection. That is the
// deliberate fail-closed direction: no credential means no control surface,
// not an unauthenticated one.
//
// Not to be confused with `nodeServiceLoginTokenState` (api/stores/shared.ts):
// that store is dead code for a *realtime-api* Clerk sign-in token that used to
// flow service -> frontend over this very socket (`maybeProvideLoginTokenToHost`,
// since removed). It is written by nobody on either side, and it could not
// carry this credential anyway — it travels over the connection being
// authenticated.
type NodeServiceTokenBridge = {
  nodeServiceAuthToken?: string;
  getNodeServiceAuthToken?: () => string | Promise<string>;
};

/**
 * Resolve the connection credential. Deliberately NOT cached: node-service
 * rotates the token on every start, so a cached value would be permanently
 * stale after a service restart and every reconnect would fail.
 */
const resolveAuthToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  const bridge = (window as unknown as { playcast?: NodeServiceTokenBridge }).playcast;
  if (!bridge) return null;

  try {
    if (typeof bridge.getNodeServiceAuthToken === 'function') {
      const token = await bridge.getNodeServiceAuthToken();
      if (typeof token === 'string' && token.length > 0) return token;
    }
    if (typeof bridge.nodeServiceAuthToken === 'string' && bridge.nodeServiceAuthToken.length > 0) {
      return bridge.nodeServiceAuthToken;
    }
  } catch (error) {
    console.error('[Node Service Client] Failed to read node-service auth token:', error);
  }

  return null;
};

const buildConnectionUrl = (token: string | null): string => {
  const base = `ws://localhost:${PORT}/`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

// Guards the async gap between connect() being called and the socket actually
// being constructed, so two callers (or a disconnect racing a pending token
// read) cannot open a second socket.
let isOpening = false;
let connectGeneration = 0;

// Message handlers
type MessageHandler = (message: PlaycastMessage<PlaycastMessageTarget>) => void;
const messageHandlers = new Map<string, MessageHandler[]>();

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startHeartbeat = () => {
  stopHeartbeat();

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      Sentry.addBreadcrumb({
        category: 'node-service',
        message: 'Heartbeat detected invalid websocket',
        level: 'warning',
        data: { readyState: wsConnection?.readyState ?? null },
      });
      stopHeartbeat();
      disconnect();
      if (shouldAutoReconnect) {
        handleReconnect();
      }
      return;
    }

    try {
      const heartbeatMessage = generators.service.ping('experimental');
      wsConnection.send(JSON.stringify(heartbeatMessage));
    } catch (error) {
      console.error('[Node Service Client] Failed to send heartbeat:', error);
      Sentry.addBreadcrumb({
        category: 'node-service',
        message: 'Heartbeat send failed',
        level: 'error',
        data: { error: error?.toString() || 'Unknown error' },
      });
      stopHeartbeat();
      disconnect();
      if (shouldAutoReconnect) {
        handleReconnect();
      }
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const handleReconnect = () => {
  if (!shouldAutoReconnect) {
    console.log('[Node Service Client] Auto-reconnect disabled');
    return;
  }

  // Check if we've exceeded max attempts
  if (reconnectAttempts >= maxReconnectAttempts) {
    console.error(`[Node Service Client] Max reconnection attempts (${maxReconnectAttempts}) reached. Stopping reconnection.`);
    shouldAutoReconnect = false;
    return;
  }

  // Prevent multiple simultaneous reconnection attempts
  if (isReconnecting) {
    console.log('[Node Service Client] Reconnection already in progress');
    return;
  }

  isReconnecting = true;
  reconnectAttempts++;

  // Calculate exponential backoff delay
  const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts - 1), 30000); // Max 30 seconds

  console.log(`[Node Service Client] Attempting to reconnect (attempt ${reconnectAttempts}/${maxReconnectAttempts}) in ${delay}ms...`);

  // Clean up existing connection if it exists
  disconnect();

  // Wait before attempting to reconnect
  clearReconnectTimeout();
  reconnectTimeout = setTimeout(() => {
    try {
      // Reset wsConnection to null before attempting to reconnect
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Node Service Client] Reconnection attempt failed:', error);
      // Don't call handleReconnect recursively here, let the connection failure handle it
    } finally {
      isReconnecting = false;
    }
  }, delay);
};

const processQueue = () => {
  while (messageQueue.length > 0 && isConnected && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        wsConnection.send(JSON.stringify(message));
        console.log('[Node Service Client] Sent queued message:', message.header.action);
      } catch (error) {
        console.error('[Node Service Client] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

const electronState = getElectronStores();
const sharedStateStores = getSharedStores();
const handleIncomingMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  console.log('[Node Service Client] Received message:', message.header.action, 'target:', message.header.target);

  // A message arriving is the first evidence the connection is genuinely
  // usable, as opposed to merely open (see the note in `onopen`). node-service
  // flushes `getNotifyMessages(['hostFrontend'])` to every client the moment it
  // accepts one, so an authorized peer lands here immediately; an unauthorized
  // one never does and keeps its accumulating backoff.
  if (reconnectAttempts !== 0) {
    console.log('[Node Service Client] Connection confirmed by first message; resetting reconnect backoff');
    reconnectAttempts = 0;
  }

  if (message.header.target === 'state' && message.header.action === 'replication') {
    console.log('[Node Service Client] Received replication message:', message);
    const stateMessage = message as PlaycastMessage<PlaycastMessageStateReplication<any>>;
    const channel = stateMessage.body.message.channel;
    if (electronState[channel]) {
      electronState[channel].updateState(stateMessage.body.message.state as never);
    } else if (sharedStateStores[channel]) {
      sharedStateStores[channel].updateState(stateMessage.body.message.state as never);
    } else {
      console.error('[Node Service Client] Invalid channel:', channel);
    }
    return;
  }

  // Call registered handlers for this action
  const handlers = messageHandlers.get(message.header.action);
  if (handlers && handlers.length > 0) {
    handlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error(`[Node Service Client] Error in message handler for ${message.header.action}:`, error);
      }
    });
  } else {
    console.log(`[Node Service Client] No handlers registered for action: ${message.header.action}`);
  }
};

const openConnection = (token: string | null) => {
  const wsUrl = buildConnectionUrl(token); // Node service WebSocket server
  console.log('[Node Service Client] Connecting to: ws://localhost:' + PORT);

  try {
    wsConnection = new WebSocket(wsUrl);

    wsConnection.onopen = () => {
      console.log('[Node Service Client] Connected to node service');

      // NOT reset here. `onopen` only proves a TCP+upgrade handshake completed,
      // which is not the same as being authenticated: node-service can accept
      // the socket and then close it, and a rejection that arrives after OPEN
      // would reset the backoff on every cycle — turning the capped exponential
      // retry below into an uncapped ~1 Hz loop that never stops and logs two
      // errors per cycle. The counter is cleared in handleIncomingMessage
      // instead, on the first message that actually arrives from the service,
      // which is proof the connection is usable.

      isConnected = true;
      startHeartbeat();
      processQueue();
      const replicationMessages = getNotifyMessages(['nodeService']);
      replicationMessages.nodeService.forEach((message) => {
        console.log('[Node Service Client] Sending replication message:', message.header.action);
        sendMessage(message);
      });
    };

    wsConnection.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PlaycastMessage<PlaycastMessageTarget>;
        handleIncomingMessage(message);
      } catch (error) {
        console.error('[Node Service Client] Failed to parse message:', error);
      }
    };

    wsConnection.onclose = (event) => {
      console.log('[Node Service Client] Connection closed:', event.code, event.reason);
      isConnected = false;
      stopHeartbeat();

      // Always attempt reconnect if auto-reconnect is enabled
      if (shouldAutoReconnect && !isReconnecting) {
        console.log('[Node Service Client] Connection lost, attempting to reconnect...');
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Node Service Client] WebSocket error:', error);
      // Don't call handleReconnect here - let onclose handle it
    };
  } catch (error) {
    console.error('[Node Service Client] Failed to connect:', error);
    if (shouldAutoReconnect) {
      handleReconnect();
    }
  }
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Node Service Client] Connection already exists');
    return;
  }
  if (isOpening) {
    console.warn('[Node Service Client] Connection attempt already in flight');
    return;
  }

  isOpening = true;
  const generation = ++connectGeneration;

  resolveAuthToken()
    .then((token) => {
      // A disconnect() (or another connect()) landed while we were reading the
      // token — that newer intent wins.
      if (generation !== connectGeneration) {
        console.log('[Node Service Client] Superseded connection attempt, not opening socket');
        return;
      }
      if (!token) {
        console.error(
          '[Node Service Client] No node-service auth token available; node-service will reject this connection. ' +
            'host-electron must expose window.playcast.nodeServiceAuthToken read from ' +
            'C:\\ProgramData\\Playcast\\node-service\\ws-auth-token.',
        );
      }
      openConnection(token);
    })
    .catch((error) => {
      console.error('[Node Service Client] Failed to start connection:', error);
      if (shouldAutoReconnect) {
        handleReconnect();
      }
    })
    .finally(() => {
      if (generation === connectGeneration) {
        isOpening = false;
      }
    });
};

export const disconnect = (disableAutoReconnect = false) => {
  console.log('[Node Service Client] Disconnecting...', disableAutoReconnect ? '(auto-reconnect disabled)' : '');

  if (disableAutoReconnect) {
    shouldAutoReconnect = false;
  }

  stopHeartbeat();
  clearReconnectTimeout();
  isConnected = false;
  messageQueue = [];
  // Invalidate any in-flight token read so its socket is never opened.
  connectGeneration++;
  isOpening = false;

  if (wsConnection) {
    // Store reference and null the connection before closing
    const conn = wsConnection;
    wsConnection = null;

    try {
      conn.close(1000, 'Client disconnect');
    } catch (error) {
      console.error('[Node Service Client] Error while closing connection:', error);
    }
  }
};

export interface SendMessageOptions {
  /**
   * When false, a message that can't be sent right now is dropped instead of
   * queued. Use this for periodic "latest snapshot wins" messages (e.g. a
   * software-inventory poll reply) where queueing while disconnected would
   * only accumulate stale snapshots to flush all at once on reconnect; the
   * next periodic poll will produce a fresher one anyway. Defaults to true
   * to preserve existing at-least-once delivery for other message types.
   */
  queueOnFailure?: boolean;
}

export const sendMessage = (
  message: PlaycastMessage<PlaycastMessageTarget>,
  options: SendMessageOptions = {},
) => {
  const { queueOnFailure = true } = options;

  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isConnected) {
    if (queueOnFailure) {
      console.warn('[Node Service Client] Connection not ready, queueing message:', message.header.action);
      messageQueue.push(message);
    } else {
      console.warn('[Node Service Client] Connection not ready, dropping message:', message.header.action);
    }
    return false;
  }

  try {
    wsConnection.send(JSON.stringify(message));
    console.log('[Node Service Client] Sent message:', message.header.action);
    return true;
  } catch (error) {
    console.error('[Node Service Client] Failed to send message:', error);
    if (queueOnFailure) {
      messageQueue.push(message);
    }
    return false;
  }
};

// Handler registration system
export const onMessage = (action: string, handler: MessageHandler) => {
  if (!messageHandlers.has(action)) {
    messageHandlers.set(action, []);
  }
  messageHandlers.get(action)?.push(handler);

  console.log(`[Node Service Client] Registered handler for action: ${action}`);
};

export const offMessage = (action: string, handler: MessageHandler) => {
  const handlers = messageHandlers.get(action);
  if (handlers) {
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      console.log(`[Node Service Client] Unregistered handler for action: ${action}`);
    }
  }
};

export const clearMessageHandlers = (action?: string) => {
  if (action) {
    messageHandlers.delete(action);
    console.log(`[Node Service Client] Cleared handlers for action: ${action}`);
  } else {
    messageHandlers.clear();
    console.log('[Node Service Client] Cleared all message handlers');
  }
};

// Utility functions
export const isNodeServiceConnected = (): boolean => {
  return wsConnection?.readyState === WebSocket.OPEN && isConnected;
};

export const getConnectionStatus = () => {
  return {
    connected: isNodeServiceConnected(),
    reconnecting: isReconnecting,
    queueSize: messageQueue.length,
    autoReconnect: shouldAutoReconnect,
    reconnectAttempts,
    maxReconnectAttempts,
    connectionState: wsConnection?.readyState ?? -1,
  };
};

export const setAutoReconnect = (enabled: boolean) => {
  shouldAutoReconnect = enabled;
  console.log(`[Node Service Client] Auto-reconnect ${enabled ? 'enabled' : 'disabled'}`);
};

// Additional utility functions for controlling reconnection behavior
export const enableAutoReconnect = () => {
  shouldAutoReconnect = true;
  reconnectAttempts = 0; // Reset attempts when re-enabling
  console.log('[Node Service Client] Auto-reconnect enabled');
};

export const disableAutoReconnect = () => {
  shouldAutoReconnect = false;
  clearReconnectTimeout();
  console.log('[Node Service Client] Auto-reconnect disabled');
};

export const forceReconnect = () => {
  console.log('[Node Service Client] Forcing reconnection...');
  reconnectAttempts = 0; // Reset attempts for forced reconnect
  shouldAutoReconnect = true;
  handleReconnect();
};

addReplicationStateChangeCallback('nodeService', (message) => {
  console.log('[Node Service Client] Received replication message:', message);
  sendMessage(message);
});
